import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  Container,
  IconButton,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Stack,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Menu,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Pagination,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Email,
  LockReset,
  Assignment,
  Visibility,
  VisibilityOff,
  ArrowDropDown,
  Payment,
  ContentCopy,
  Link,
  PictureAsPdf,
  Download,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { AgentService, ClientFullDetailsResponse, GetProductFullDataPayload } from "../../services/agent.service";
import { showSnackbar } from "../../store/system.store";
import { StoreState } from "../../store/store";
import ProductActionsModal from "./products-action";
import { FetchedFile } from "../../models/uploaded-file";
import { format } from "date-fns";

const productOptions = [
  {
    value: "trackMaster",
    label: "Track Master",
    route: "/product/track-master",
  },
  {
    value: "webIntelligence",
    label: "Web Intelligence",
    route: "/product/web-intelligence",
  },
  {
    value: "correspondence",
    label: "Correspondence",
    route: "/product/correspondence",
  },
  {
    value: "blockchainAnalysis",
    label: "Blockchain Analysis",
    route: "/product/blockchain-analysis",
  },
];

const AdminClientDetailsPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: StoreState) => state.user.loggedInUser);
  const isAdmin = user?.role === "admin";
  const [productModalIsOpen, setProductModalIsOpen] = useState(false);

  const [productMetadata, setProductMetadata] = useState<GetProductFullDataPayload | null>(null);

  const [client, setClient] = useState<ClientFullDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<string>("");
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordMenuAnchor, setPasswordMenuAnchor] = useState<null | HTMLElement>(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedProductForPayment, setSelectedProductForPayment] = useState<string>("");
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [kycEnabled, setKycEnabled] = useState<boolean>(true);
  const [clientFiles, setClientFiles] = useState<{
    data: { list: FetchedFile[]; total: number } | null;
    error: string | null;
    loading: boolean;
    pagination: {
      page: number;
      limit: number;
      totalCount?: number;
      totalPages?: number;
    };
  }>({
    data: null,
    error: null,
    loading: false,
    pagination: {
      page: 1,
      limit: 4,
    },
  });
  const [isGeneratingPaymentLink, setIsGeneratingPaymentLink] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const onToggleProductModal = () => {
    setProductModalIsOpen((prev) => !prev);
  };
  const handleSelectProduct = (payload: GetProductFullDataPayload) => {
    setProductMetadata(payload);
  };

  useEffect(() => {
    const fetchClientFiles = async (clientCrmProfileId: string) => {
      try {
        setClientFiles((prev) => ({
          ...prev,
          loading: true,
          error: null,
        }));

        const response = await AgentService.getAllClientFiles({
          clientCrmProfileId,
          page: clientFiles?.pagination.page || 1,
          limit: clientFiles?.pagination.limit || 10,
        });
        setClientFiles((prev) => ({
          ...prev,
          loading: false,
          data: response,
          error: null,
          pagination: {
            ...prev.pagination,
            totalCount: response.total,
            totalPages: Math.ceil(response.total / (prev.pagination.limit || 10)),
          },
        }));
      } catch (err) {
        console.log("Error in fetchClientFiles: ", err);
        setClientFiles((prev) => ({
          ...prev,
          loading: false,
          data: null,
          error: "Failed to fetch client files",
        }));
      }
    };

    if (clientId) {
      fetchClientFiles(clientId);
    }
  }, [clientId]);

  // Add these functions to your component
  const handleFileAction = async (file: FetchedFile, action: "preview" | "download") => {
    try {
      const fileForService = {
        id: file.id,
        createdAt: file.Created_Time,
        content: {
          name: file.File_Name,
          size: Number(file.Size),
          type: "pdf",
        },
      };
      await AgentService.handleCrmFileAction(fileForService, action, clientId!);
    } catch (error) {
      console.error(`Error ${action}ing file:`, error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: `Failed to ${action} file. Please try again.`,
        })
      );
    }
  };
  const handleFilesPageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    if (!clientId) return;

    setClientFiles((prev) => ({
      ...prev,
      loading: true,
      data: null,
      error: null,
      pagination: { ...prev.pagination, page },
    }));
    // Refetch with new page
    const fetchPage = async () => {
      try {
        const response = await AgentService.getAllClientFiles({
          clientCrmProfileId: clientId,
          page,
          limit: clientFiles.pagination.limit,
        });
        setClientFiles((prev) => ({
          ...prev,
          loading: false,
          data: response,
          error: null,
          pagination: {
            ...prev.pagination,
            totalCount: response.total,
            totalPages: Math.ceil(response.total / prev.pagination.limit),
          },
        }));
      } catch (err) {
        console.log("Error in handleFilesPageChange: ", err);
        setClientFiles((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to fetch client files",
        }));
      }
    };

    fetchPage();
  };

  // open product full data modal when productMetadata is set
  useEffect(() => {
    if (productMetadata) {
      onToggleProductModal();
    }
  }, [productMetadata?.productId]);

  // reset productMetadata when modal is closed
  useEffect(() => {
    if (!productModalIsOpen) {
      setProductMetadata(null);
    }
  }, [productModalIsOpen]);

  const LoadingSkeleton = () => (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header Skeleton */}
      <Box
        sx={{
          padding: { xs: "16px", md: "24px" },
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
          <Skeleton variant="text" width="30%" height={32} />
          <Skeleton variant="rounded" width={80} height={24} />
        </Box>

        <Grid container spacing={3}>
          {/* Product Request Card Skeleton */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden", height: "280px" }}>
              <Box sx={{ p: 2, bgcolor: theme.palette.grey[200] }}>
                <Skeleton variant="text" width="70%" height={24} />
              </Box>
              <Box sx={{ p: 3 }}>
                <Skeleton variant="text" width="90%" height={16} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={36} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Paper>
          </Grid>

          {/* Client Information Skeleton */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "280px" }}>
              <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <Grid key={index} item xs={6}>
                    <Skeleton variant="text" width="50%" height={14} />
                    <Skeleton variant="text" width="80%" height={16} />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Actions Skeleton */}
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "280px" }}>
              <Skeleton variant="text" width="60%" height={24} sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Products Table Skeleton */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, bgcolor: "grey.50" }}>
        <Paper elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
          <Box
            sx={{
              p: 3,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "white",
            }}
          >
            <Skeleton variant="text" width="40%" height={28} />
          </Box>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableCell key={index} sx={{ bgcolor: theme.palette.grey[100] }}>
                      <Skeleton variant="text" width="80%" height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from({ length: 6 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array.from({ length: 5 }).map((_, cellIndex) => (
                      <TableCell key={cellIndex}>
                        <Skeleton
                          variant={cellIndex === 1 || cellIndex === 3 ? "rounded" : "text"}
                          width={cellIndex === 1 || cellIndex === 3 ? "60%" : "90%"}
                          height={cellIndex === 1 || cellIndex === 3 ? 24 : 20}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {/* Client Files Section */}
        <Box sx={{ mt: 3 }}>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Client Files
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              {clientFiles?.loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : clientFiles?.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {clientFiles.error}
                </Alert>
              ) : null}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );

  const handleCreateProductRequest = () => {
    if (!selectedProduct || !client) return;

    const selectedOption = productOptions.find((option) => option.value === selectedProduct);
    if (!selectedOption) return;

    // Product requests are owned by the client's platform user; without it the backend rejects the request
    if (!client.client.id) {
      dispatch(
        showSnackbar({
          isError: true,
          msg: "This client has no platform account linked to their CRM profile. Please contact support to link the account before creating a product request.",
          disableAutoHide: true,
        })
      );
      return;
    }

    const navigationState = {
      clientData: {
        crmProfileId: client.client.crmProfileId,
        id: client.client.id,
        name: client.client.fullName,
        email: client.client.email,
        totalAmount: totalAmount,
      },
    };

    navigate(selectedOption.route, {
      state: navigationState,
    });

    // Reset form after navigation
    setSelectedProduct("");
    setTotalAmount("");
  };

  const fetchClientDetails = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await AgentService.getClientDetails(id);

      if (response) {
        setClient(response);
      } else {
        setError("Client not found");
        dispatch(
          showSnackbar({
            isError: true,
            msg: "Client not found",
          })
        );
      }
    } catch (error) {
      console.error("Error fetching client details:", error);
      setError("Failed to load client details");
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to load client details. Please try again.",
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchClientDetails(clientId);
    }
  }, [clientId]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!client && !isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || "Client not found. Please check the client ID and try again."}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button onClick={() => navigate("/agent/clients")} variant="outlined">
            Back to Client List
          </Button>
        </Box>
      </Container>
    );
  }

  if (!client) {
    return <Box>client with id {clientId} not found</Box>;
  }

  const handleSendResetPasswordEmail = async () => {
    try {
      await AgentService.resendPasswordReset(client.client.email);
      dispatch(
        showSnackbar({
          isError: false,
          msg: "Password reset email sent successfully",
        })
      );
    } catch (error) {
      console.error("Error sending password reset email:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to send password reset email. Please try again.",
        })
      );
    }
  };

  const handleSendEmail = () => {
    const recipient = client.client.email;

    const mailtoLink = `mailto:${recipient}}`;

    window.location.href = mailtoLink;
  };

  const handlePasswordMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setPasswordMenuAnchor(event.currentTarget);
  };

  const handlePasswordMenuClose = () => {
    setPasswordMenuAnchor(null);
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    handlePasswordMenuClose();
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setNewPassword("");
    setShowPassword(false);
  };

  const handleSetPassword = async () => {
    if (!newPassword.trim() || !client) return;

    try {
      await AgentService.setClientPassword(client.client.email, newPassword);
      dispatch(
        showSnackbar({
          isError: false,
          msg: "Password updated successfully",
        })
      );
      handleClosePasswordDialog();
    } catch (error) {
      console.error("Error setting password:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to set password. Please try again.",
        })
      );
    }
  };

  const handleSendResetPasswordEmailFromMenu = () => {
    handlePasswordMenuClose();
    handleSendResetPasswordEmail();
  };

  const handleOpenPaymentDialog = () => {
    setKycEnabled(true); // Ensure KYC is enabled when dialog opens
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedProductForPayment("");
    setPaymentLink("");
    setKycEnabled(true);
    setIsGeneratingPaymentLink(false);
  };

  const handleGeneratePaymentLink = async () => {
    if (!selectedProductForPayment || !client) return;

    setIsGeneratingPaymentLink(true);

    try {
      // Update KYC status first
      await AgentService.updateClientKYCStatus(client.client.crmProfileId, kycEnabled ? "Yes" : "No");

      // Generate payment link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/payment?productRequestId=${selectedProductForPayment}`;
      setPaymentLink(link);

      dispatch(
        showSnackbar({
          isError: false,
          msg: `Payment link generated. 3D Secure: ${kycEnabled ? "Enabled" : "Disabled"}`,
        })
      );
    } catch (error) {
      console.error("Error generating payment link:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to generate payment link. Please try again.",
        })
      );
    } finally {
      setIsGeneratingPaymentLink(false);
    }
  };

  const handleCopyPaymentLink = async () => {
    if (!paymentLink) return;

    try {
      await navigator.clipboard.writeText(paymentLink);
      dispatch(
        showSnackbar({
          isError: false,
          msg: "Payment link copied to clipboard",
        })
      );
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to copy link to clipboard",
        })
      );
    }
  };

  const handleCopyReviewLink = async () => {
    if (!client) return;

    const userId = client.client.id;
    const link = `https://www.personaI-reviews.com/2022/11/ic-crypto-review-ic-crypto-com-scam/?share=${userId}`;

    try {
      await navigator.clipboard.writeText(link);
      dispatch(
        showSnackbar({
          isError: false,
          msg: "Track Master link copied to clipboard",
        })
      );
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      dispatch(
        showSnackbar({
          isError: true,
          msg: "Failed to copy track master link to clipboard",
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {/* Header with Client Info and Actions - Improved */}
      <Box
        sx={{
          padding: { xs: "16px", md: "24px" },
          backgroundColor: theme.palette.grey[50],
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <IconButton
            onClick={() => navigate("/agent/clients")}
            sx={{
              p: 1,
              bgcolor: "white",
              boxShadow: 1,
              "&:hover": { bgcolor: theme.palette.grey[100] },
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 600 }}>
            {client.client.fullName}
          </Typography>
          <Chip label={client.client.clientStatus} color="primary" variant="filled" sx={{ fontWeight: 600 }} />
        </Box>

        <Grid container spacing={3}>
          {/* Product Request Card - Enhanced */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                height: "100%",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontWeight: 600,
                  }}
                >
                  <Assignment />
                  Create Product Request
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Create a new product request for this client
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="product-select-label">Select Product</InputLabel>
                  <Select
                    labelId="product-select-label"
                    value={selectedProduct}
                    label="Select Product"
                    onChange={(e) => {
                      setSelectedProduct(e.target.value);
                      setTotalAmount("0"); // Reset amount when product changes
                    }}
                    size="small"
                  >
                    {productOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label="Total Amount ($)"
                  variant="outlined"
                  size="small"
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="0.00"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<Assignment />}
                  onClick={handleCreateProductRequest}
                  disabled={!selectedProduct}
                  color="primary"
                  sx={{ py: 1.2 }}
                >
                  Create Request
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Client Information - Enhanced */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                Client Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Email
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {client.client.email}
                  </Typography>
                </Grid>
                {isAdmin && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Phone
                    </Typography>
                    <Typography variant="body2">{client.client.phone}</Typography>
                  </Grid>
                )}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Country
                  </Typography>
                  <Typography variant="body2">{client.client.country}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Risk Level
                  </Typography>
                  <Chip
                    label={client.client.riskLevel}
                    color={client.client.riskLevel === "Low" ? "success" : client.client.riskLevel === "Medium" ? "warning" : "error"}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Owner
                  </Typography>
                  <Typography variant="body2">{client.owner.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total Paid
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "success.main" }}>
                    ${client.totalPaidAmount.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Products
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {client.totalProducts}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Last Activity
                  </Typography>
                  <Typography variant="body2">{new Date(client.client.lastActivityTime).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Actions - Enhanced */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                height: "100%",
                transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                }}
              >
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<LockReset />}
                  endIcon={<ArrowDropDown />}
                  onClick={handlePasswordMenuClick}
                  fullWidth
                  color="warning"
                  sx={{ py: 1.2 }}
                >
                  Reset Password
                </Button>

                <Menu anchorEl={passwordMenuAnchor} open={Boolean(passwordMenuAnchor)} onClose={handlePasswordMenuClose}>
                  <MenuItem onClick={handleSendResetPasswordEmailFromMenu}>
                    <LockReset sx={{ mr: 1 }} />
                    Send Reset Email
                  </MenuItem>
                  <MenuItem onClick={handleOpenPasswordDialog}>
                    <LockReset sx={{ mr: 1 }} />
                    Set New Password
                  </MenuItem>
                </Menu>

                <Button variant="outlined" startIcon={<Email />} onClick={handleSendEmail} fullWidth sx={{ py: 1.2 }}>
                  Send Email
                </Button>

                {isAdmin && (
                  <Button variant="contained" startIcon={<Payment />} onClick={handleOpenPaymentDialog} fullWidth color="success" sx={{ py: 1.2 }}>
                    Create Payment Link
                  </Button>
                )}

                <Button variant="outlined" startIcon={<Link />} onClick={handleCopyReviewLink} fullWidth color="info" sx={{ py: 1.2 }}>
                  Copy Track-Master Link
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Products Table - Enhanced */}
      <Box sx={{ flex: 1, p: { xs: 2, md: 3 }, bgcolor: "grey.50" }}>
        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              p: 3,
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              Product Requests ({client.totalProducts})
              <Chip
                label={`${client.products.filter((p) => p.payed).length} Paid / ${client.products.filter((p) => !p.payed).length} Unpaid`}
                size="small"
                color="info"
                variant="outlined"
              />
            </Typography>
          </Box>

          <TableContainer
            sx={{
              maxHeight: { xs: 400, md: "calc(100vh - 480px)" },
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      fontWeight: "bold",
                    }}
                  >
                    Type
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      fontWeight: "bold",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      fontWeight: "bold",
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    Created
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      fontWeight: "bold",
                    }}
                  >
                    Paid
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: theme.palette.grey[100],
                      fontWeight: "bold",
                      display: { xs: "none", md: "table-cell" },
                    }}
                  >
                    Sum to Pay
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {client.products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      transition: "background-color 0.2s ease-in-out",
                    }}
                    onClick={() =>
                      handleSelectProduct({
                        clientCrmProfileId: clientId!,
                        productId: product.id,
                        productRequestType: product.type,
                      })
                    }
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{product.type}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.status}
                        color={product.status === "Done" ? "success" : product.status === "Pending" ? "warning" : "info"}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                      <Typography variant="body2">{new Date(product.createdTime).toLocaleDateString()}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(product.createdTime).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.payed ? "Paid" : "Unpaid"}
                        color={product.payed ? "success" : "error"}
                        size="small"
                        variant={product.payed ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: product.leftToPay ? 600 : 400,
                          color: product.leftToPay ? "error.main" : "text.secondary",
                        }}
                      >
                        {product.leftToPay ? `$${product.leftToPay.toLocaleString()}` : "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Client Files Section */}
        </Paper>
        <Box sx={{ mt: 3 }}>
          <Paper
            elevation={2}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ p: 3, borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Client Files
              </Typography>
            </Box>

            <Box sx={{ p: 3 }}>
              {clientFiles?.loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : clientFiles?.error ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {clientFiles.error}
                </Alert>
              ) : clientFiles?.data?.list?.length ? (
                <>
                  <Grid container spacing={2}>
                    {clientFiles.data.list.map((file) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            height: "200px",
                            display: "flex",
                            flexDirection: "column",
                            transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                            "&:hover": {
                              transform: "translateY(-2px)",
                              boxShadow: 3,
                            },
                          }}
                        >
                          {/* File Icon */}
                          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                            <PictureAsPdf
                              sx={{
                                fontSize: 40,
                                color: "error.main",
                              }}
                            />
                          </Box>

                          {/* File Info */}
                          <Box sx={{ overflow: "hidden" }}>
                            <Tooltip title={file.File_Name}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  textAlign: "center",
                                  mb: 1,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {file.File_Name}
                              </Typography>
                            </Tooltip>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 0.5 }}>
                              {file.Size}
                            </Typography>

                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center", mb: 1 }}>
                              {format(new Date(file.Created_Time), "dd/MM/yy HH:mm")}
                            </Typography>

                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "block",
                                textAlign: "center",
                                fontSize: "0.65rem",
                              }}
                            >
                              By: {file.Owner.name}
                            </Typography>
                          </Box>

                          {/* Action Buttons */}
                          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleFileAction(file, "preview")}
                              startIcon={<Visibility />}
                              sx={{
                                flex: 1,
                                fontSize: "0.7rem",
                                py: 0.5,
                              }}
                            />

                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleFileAction(file, "download")}
                              startIcon={<Download />}
                              sx={{
                                flex: 1,
                                fontSize: "0.7rem",
                                py: 0.5,
                              }}
                            />
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Pagination */}
                  {clientFiles.pagination?.totalPages && clientFiles.pagination.totalPages > 1 && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                      <Pagination
                        count={clientFiles.pagination.totalPages}
                        page={clientFiles.pagination.page}
                        onChange={handleFilesPageChange}
                        color="primary"
                        size="medium"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  No files available
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Set New Password for {client?.client.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter a new password for {client?.client.email}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <TextField
            autoFocus
            fullWidth
            label="New Password"
            type={showPassword ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            variant="outlined"
            sx={{ mb: 2, pt: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Alert severity="info" sx={{ mt: 1 }}>
            The client will be able to log in immediately with this new password. No email will be sent.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClosePasswordDialog} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleSetPassword} variant="contained" disabled={!newPassword.trim()} sx={{ ml: 1 }}>
            Set Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Create Payment Link for {client?.client.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate a payment link for a specific product request
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <FormControlLabel
            control={<Switch checked={kycEnabled} onChange={(e) => setKycEnabled(e.target.checked)} color="primary" />}
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Enable 3D Secure Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {kycEnabled ? "KYC verification will be required for payment" : "Payment will process without additional verification"}
                </Typography>
              </Box>
            }
            sx={{ mb: 3, alignItems: "flex-start" }}
          />

          <Divider sx={{ mb: 3 }} />

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="payment-product-select-label">Select Product Request</InputLabel>
            <Select
              labelId="payment-product-select-label"
              value={selectedProductForPayment}
              label="Select Product Request"
              onChange={(e) => setSelectedProductForPayment(e.target.value)}
            >
              {client?.products
                .filter((product) => !product.payed)
                .map((product) => (
                  <MenuItem key={product.id} value={product.id}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <Typography>{product.type}</Typography>
                      <Typography color="text.secondary" sx={{ ml: 2 }}>
                        {new Date(product.createdTime).toLocaleDateString()} - ${product.leftToPay?.toLocaleString() || "N/A"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {paymentLink && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Payment Link Generated:
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  value={paymentLink}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Button variant="contained" startIcon={<ContentCopy />} onClick={handleCopyPaymentLink} sx={{ minWidth: "auto", px: 2 }}>
                  Copy
                </Button>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Share this link with the client to allow them to make a payment for the selected product request.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClosePaymentDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleGeneratePaymentLink}
            variant="contained"
            disabled={!selectedProductForPayment || isGeneratingPaymentLink}
            sx={{ ml: 1 }}
            startIcon={isGeneratingPaymentLink ? <CircularProgress size={20} color="inherit" /> : undefined}
          >
            {isGeneratingPaymentLink ? "Generating..." : "Generate Link"}
          </Button>
        </DialogActions>
      </Dialog>
      {productModalIsOpen && productMetadata ? (
        <ProductActionsModal
          userId={client.client.id}
          isOpen={productModalIsOpen}
          onClose={onToggleProductModal}
          productMetaData={{
            crmProfileId: productMetadata.clientCrmProfileId,
            productId: productMetadata.productId,
            productRequestType: productMetadata.productRequestType,
          }}
          clientName={client.client.fullName}
          clientEmail={client.client.email}
          ownerEmail={client.owner.email}
        />
      ) : null}
    </Box>
  );
};

export default AdminClientDetailsPage;
